import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Download, ShieldAlert, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

import { ReportSearch } from "@/components/report-search";
import { DisclaimerNote, SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import { StalenessCallout } from "@/components/staleness-notice";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import type { AnnotationSummary } from "@/lib/annotation.server";
import { formatCount } from "@/lib/format";
import { deleteJob, getJobWithResult, reannotateJob } from "@/lib/jobs.functions";
import { parseReportSearchParams } from "@/lib/report-search-params";

export const Route = createFileRoute("/_authenticated/report/$jobId")({
  // ?q=BRCA1&scope=gene opens "Search your full file" already filled in (genome.coach's chat links here).
  validateSearch: parseReportSearchParams,
  head: () => ({
    meta: [
      { title: "Your annotated results — Understand Your Genome" },
      {
        name: "description",
        content:
          "Your DNA file, annotated against public genetic databases: variant frequencies, known associations and medication-response notes where they exist.",
      },
      { property: "og:title", content: "Your annotated results — Understand Your Genome" },
      {
        property: "og:description",
        content: "Your variants, annotated with what's publicly known about them.",
      },
    ],
  }),
  component: ReportPage,
});

function ReportPage() {
  const { jobId } = Route.useParams();
  const { q: searchTerm, scope: searchScope } = Route.useSearch();
  const fetchReport = useServerFn(getJobWithResult);
  const removeJob = useServerFn(deleteJob);
  const reannotateFn = useServerFn(reannotateJob);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["report", jobId],
    queryFn: () => fetchReport({ data: { jobId } }),
    refetchInterval: (query) =>
      query.state.data?.job && query.state.data.job.status !== "ready" ? 4000 : false,
  });

  const reannotateMutation = useMutation({
    mutationFn: () => reannotateFn({ data: { jobId } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["report", jobId] }),
    onError: (error) =>
      toast.error(
        error instanceof Error ? error.message : "Couldn't reprocess this sample. Please try again.",
      ),
  });

  const job = data?.job ?? null;
  const summary = (data?.result?.summary ?? null) as AnnotationSummary | null;
  const isOwner = data?.isOwner ?? true;
  const share = data?.share ?? null;
  const staleness = data?.staleness ?? null;
  const canDownload = isOwner || Boolean(share?.canDownload);

  async function handleDownload() {
    const { data: session } = await supabase.auth.getSession();
    const token = session.session?.access_token;
    if (!token) {
      toast.error("Please sign in again to download this file.");
      return;
    }
    try {
      const response = await fetch(`/api/download/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`download failed: ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(job?.label || job?.filename || "results").replace(/\W+/g, "-")}-annotated.tsv.gz`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("We couldn't download that file just now. Please try again.");
    }
  }

  async function handleDelete() {
    if (!window.confirm("Delete this upload and everything we found in it? This can't be undone."))
      return;
    try {
      await removeJob({ data: { jobId } });
      toast.success("Upload and results deleted.");
      navigate({ to: "/dashboard" });
    } catch {
      toast.error("We couldn't delete that just now. Please try again.");
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-3xl flex-1 px-5 py-14">
        {isLoading && <p className="text-sm text-muted-foreground">Opening your results…</p>}

        {!isLoading && !job && (
          <div className="rounded-3xl border border-border bg-card p-10 text-center">
            <h1 className="text-2xl">We couldn't find that upload</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              It may have been deleted. Your other uploads are on your dashboard.
            </p>
            <Button asChild className="mt-6">
              <Link to="/dashboard">Back to my uploads</Link>
            </Button>
          </div>
        )}

        {job && job.status !== "ready" && (
          <div className="rounded-3xl border border-border bg-card p-10 text-center">
            <h1 className="text-2xl">Still working on it</h1>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
              We're still annotating <span className="font-medium">{job.label || job.filename}</span> — matching
              each of your variants against public databases. This page updates itself, and we'll
              email you when it's done.
            </p>
          </div>
        )}

        {job && job.status === "ready" && summary && (
          <>
            <header>
              <h1 className="text-3xl">Your annotated results</h1>
              <p className="mt-2 break-all text-sm text-muted-foreground">
                Based on {job.label || job.filename}, compared against the {summary.reference_genome} reference
                genome (the standard "template" copy of human DNA that yours is measured against).
              </p>
              {!isOwner && (
                <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Users className="size-3.5" /> Shared by {share?.ownerEmail ?? "someone"}
                </p>
              )}
            </header>

            <DisclaimerNote className="mt-6" />

            <section className="mt-10">
              <h2 className="text-2xl">Overview</h2>
              <p className="mt-2 leading-relaxed text-muted-foreground">
                {summary.plain_summary}
              </p>
              <div className="mt-6 rounded-3xl border border-border bg-card p-6">
                <p className="font-display text-3xl text-primary">
                  {formatCount(summary.total_variants)}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  variants found — places where your DNA differs from the reference. This many is
                  completely normal.
                </p>
              </div>
              <div className="mt-4 flex flex-wrap items-start gap-3">
                {canDownload && (
                  <Button onClick={handleDownload} variant="outline">
                    <Download className="size-4" /> Download annotated results
                  </Button>
                )}
                {isOwner && (
                  <Button onClick={handleDelete} variant="ghost" className="text-destructive">
                    <Trash2 className="size-4" /> Delete this upload and its results
                  </Button>
                )}
              </div>
            </section>

            <ReportSearch jobId={jobId} initialTerm={searchTerm} initialScope={searchScope} />

            {isOwner && staleness?.hasBenefit && (
              <StalenessCallout
                staleness={staleness}
                pending={reannotateMutation.isPending}
                onReprocess={() => reannotateMutation.mutate()}
              />
            )}

            <section className="mt-12 rounded-3xl border border-border bg-secondary/50 p-6">
              <h2 className="flex items-center gap-2 text-xl">
                <ShieldAlert className="size-5 text-primary" /> What to do next
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                If anything above feels relevant to your health, take these results to a doctor or a
                genetic counselor. They can weigh them against your family history and symptoms —
                something a file alone can never do.
              </p>
            </section>

            {summary.pipeline_version && (
              <p className="mt-6 text-xs text-muted-foreground/70">
                Processed with pipeline{" "}
                <code className="font-mono">{summary.pipeline_version}</code>
                {summary.pipeline_generated_at &&
                  ` on ${new Date(summary.pipeline_generated_at).toLocaleDateString()}`}
                .
              </p>
            )}
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
